// ✅ src/components/screens/ProductsScreen.js
// ✅ DEBUG REMOVED
// ✅ Delete confirm is now IN-APP modal (no "localhost says")
// ✅ QR Code modal added (opens publicStoreUrl)
// ✅ FIX: PRICE LOGIC NOW MATCHES CheckoutScreen
//    - salePrice (>0) is final (no extra discount)
//    - else apply hasDiscount/discountPercent to base price

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import QRCode from "react-qr-code";
import { auth, db } from "../../lib/firebase";
import { onAuthStateChanged } from "firebase/auth";
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  limit,
} from "firebase/firestore";

function safe(v) {
  return String(v ?? "").trim();
}
function moneyGHS(n) {
  const num = Number(n || 0);
  if (Number.isNaN(num)) return "GHS 0";
  return `GHS ${num.toLocaleString()}`;
}
function tsToMs(ts) {
  try {
    if (!ts) return null;
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    const ms = d instanceof Date ? d.getTime() : NaN;
    if (Number.isNaN(ms)) return null;
    return ms;
  } catch {
    return null;
  }
}

/* ✅ PRICE HELPERS (MATCH CheckoutScreen) */
function getBasePriceValue(p) {
  const candidates = [p?.price, p?.amount, p?.unitPrice, p?.priceGHS, p?.priceValue];

  let base = 0;
  for (const c of candidates) {
    const n = Number(c);
    if (!Number.isNaN(n) && n > 0) {
      base = n;
      break;
    }
  }

  // allow explicit 0
  if (!base) {
    const zero = Number(p?.price);
    if (!Number.isNaN(zero)) base = zero;
  }

  return base;
}

function getFinalPriceValue(p) {
  // 1) salePrice = already-final (no extra discount)
  const sale = Number(p?.salePrice);
  if (!Number.isNaN(sale) && sale > 0) return sale;

  // 2) base price
  const base = getBasePriceValue(p);

  // 3) apply discountPercent if hasDiscount OR discountPercent > 0
  const hasDiscount = !!p?.hasDiscount || Number(p?.discountPercent || 0) > 0;
  const discountPercent = Math.max(0, Math.min(95, Number(p?.discountPercent || 0)));

  if (!hasDiscount || discountPercent <= 0) return base;

  return Math.round((base * (100 - discountPercent)) / 100);
}

export default function ProductsScreen({ goBack }) {
  const navigate = useNavigate();

  /** @type {[import("firebase/auth").User|null, Function]} */
  const [user, setUser] = useState(null);

  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [subscription, setSubscription] = useState(null);

  const [loading, setLoading] = useState(true);
  const [gateLoading, setGateLoading] = useState(true);

  const [msg, setMsg] = useState("");

  /** @type {[any, Function]} */
  const [confirmProduct, setConfirmProduct] = useState(null);
  /** @type {[boolean, Function]} */
  const [confirmOpen, setConfirmOpen] = useState(false);
  /** @type {[boolean, Function]} */
  const [deleting, setDeleting] = useState(false);

  /** @type {[boolean, Function]} */
  const [qrOpen, setQrOpen] = useState(false);

  const openConfirm = (p) => {
    setConfirmProduct(p);
    setConfirmOpen(true);
  };

  const closeConfirm = () => {
    if (deleting) return;
    setConfirmOpen(false);
    setConfirmProduct(null);
  };

  const openQr = () => setQrOpen(true);
  const closeQr = () => setQrOpen(false);

  // ✅ auth
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u || null);
      if (!u) {
        setMsg("Please login first.");
        setLoading(false);
        setGateLoading(false);
      } else {
        setMsg("");
      }
    });
    return () => unsub();
  }, []);

  // ✅ load store + products
  useEffect(() => {
    if (!user) return;

    let alive = true;
    const unsubs = [];

    async function boot() {
      setLoading(true);
      setMsg("");

      try {
        const storeRef = doc(db, "stores", user.uid);
        const storeSnap = await getDoc(storeRef);

        if (!storeSnap.exists()) {
          if (!alive) return;
          setMsg("No store found for this account.");
          setStore(null);
          setProducts([]);
          setLoading(false);
          return;
        }

        if (!alive) return;
        setStore({ id: user.uid, ...(storeSnap.data() || {}) });

        const qy = query(
          collection(db, "products"),
          where("storeId", "==", user.uid),
          orderBy("updatedAt", "desc")
        );

        const unsubProducts = onSnapshot(
          qy,
          (qs) => {
            if (!alive) return;
            setProducts(qs.docs.map((d) => ({ id: d.id, ...(d.data() || {}) })));
            setLoading(false);
          },
          (err) => {
            if (!alive) return;
            setMsg(err?.message || "Failed to load products.");
            setLoading(false);
          }
        );

        unsubs.push(unsubProducts);
      } catch (e) {
        if (!alive) return;
        setMsg(e?.message || "Failed to load products.");
        setLoading(false);
      }
    }

    boot();

    return () => {
      alive = false;
      unsubs.forEach((fn) => {
        try {
          fn();
        } catch {}
      });
    };
  }, [user]);

  // ✅ subscription listener (query by uid)
  useEffect(() => {
    if (!user) return;

    let alive = true;
    setGateLoading(true);

    const qy = query(
      collection(db, "subscriptions"),
      where("uid", "==", user.uid),
      limit(1)
    );

    const unsub = onSnapshot(
      qy,
      (qs) => {
        if (!alive) return;
        const first = qs.docs[0];
        setSubscription(first ? { id: first.id, ...(first.data() || {}) } : null);
        setGateLoading(false);
      },
      (e) => {
        if (!alive) return;
        setSubscription(null);
        setGateLoading(false);
        setMsg(e?.message || "Subscription listener error.");
      }
    );

    return () => {
      alive = false;
      try {
        unsub();
      } catch {}
    };
  }, [user]);

  const isAdmin = useMemo(() => {
    const role = safe(store?.role).toLowerCase();
    return role === "admin" || store?.isAdmin === true;
  }, [store]);

  const isPaid = useMemo(() => {
    if (!subscription) return false;
    const status = safe(subscription?.status).toUpperCase();
    if (status !== "ACTIVE") return false;
    const endMs = tsToMs(subscription?.currentPeriodEnd);
    if (!endMs) return false;
    return endMs > Date.now();
  }, [subscription]);

  const publicStoreUrl = useMemo(() => {
    const slug = safe(store?.storeSlug);
    const base = typeof window !== "undefined" ? window.location.origin : "";
    if (slug) return `${base}/s/${slug}`;
    return `${base}/storeById/${safe(user?.uid)}`;
  }, [store, user]);

  const copyPublicLink = async () => {
    try {
      await navigator.clipboard.writeText(publicStoreUrl);
      setMsg("✅ Store link copied.");
      setTimeout(() => setMsg(""), 1500);
    } catch {
      setMsg("Copy failed. You can manually copy from the link box.");
    }
  };

  const goAdd = () => navigate("/seller/add");
  const goAdmin = () => navigate("/seller/admin");

  const goSellerView = (p) => navigate(`/seller/products/${p.id}`);
  const goSellerEdit = (p) => navigate(`/seller/products/${p.id}/edit`);

  const toggleActive = async (p) => {
    if (!user) return;
    try {
      const currentlyActive = p?.isActive !== false;
      await updateDoc(doc(db, "products", p.id), {
        isActive: !currentlyActive,
        status: currentlyActive ? "HIDDEN" : "ACTIVE",
        updatedAt: serverTimestamp(),
      });
    } catch (e) {
      setMsg(e?.message || "Failed to update product.");
    }
  };

  const confirmDelete = async () => {
    if (!user || !confirmProduct) return;

    setDeleting(true);
    try {
      await deleteDoc(doc(db, "products", confirmProduct.id));
      setMsg("✅ Deleted.");
      setTimeout(() => setMsg(""), 1200);
      setConfirmOpen(false);
      setConfirmProduct(null);
    } catch (e) {
      setMsg(e?.message || "Failed to delete.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="ps-page">
      <style>{css}</style>

      <div className="ps-wrap">
        <div className="ps-top">
          <button className="ps-back" onClick={goBack}>
            ← Back
          </button>

          <div className="ps-title">Products</div>

          <div className="ps-top-actions">
            {isAdmin ? (
              <button className="ps-admin" onClick={goAdmin}>
                Admin
              </button>
            ) : null}

            <button className="ps-primary" onClick={goAdd}>
              + Add
            </button>
          </div>
        </div>

        {msg ? (
          <div className={`ps-msg ${msg.includes("✅") ? "" : "is-err"}`}>{msg}</div>
        ) : null}

        {/* ✅ LINK GATE CARD */}
        <div className="ps-card">
          <div className="ps-row">
            <div className="ps-subtitle">Store Link</div>

            {gateLoading ? (
              <div className="ps-note">Checking payment…</div>
            ) : isPaid ? (
              <div className="ps-link-actions">
                <button className="ps-soft" onClick={copyPublicLink}>
                  Copy
                </button>

                <button className="ps-soft" onClick={openQr}>
                  QR Code
                </button>

                <button
                  className="ps-soft"
                  onClick={() => window.open(publicStoreUrl, "_blank")}
                >
                  Preview
                </button>
              </div>
            ) : (
              <button className="ps-primary" onClick={() => navigate("/seller/pay")}>
                Generate Link
              </button>
            )}
          </div>

          {gateLoading ? null : isPaid ? (
            <>
              <input className="ps-input" readOnly value={publicStoreUrl} />
              <div className="ps-note">Share this link with customers.</div>
            </>
          ) : (
            <div className="ps-note">
              You haven’t paid yet. Click <b>Generate Link</b> to pay and activate your
              store.
            </div>
          )}
        </div>

        <div className="ps-grid">
          {loading ? (
            <div className="ps-card">Loading…</div>
          ) : products.length === 0 ? (
            <div className="ps-card">No products yet.</div>
          ) : (
            products.map((p) => {
              const title = safe(p.title) || "Untitled";
              const img =
                safe(p.thumbnailUrl) ||
                safe(p.imageUrl) ||
                safe(p?.mediaUrls?.[0]) ||
                safe(p?.imageUrls?.[0]);

              const active = p?.isActive !== false;

              // ✅ MATCH CHECKOUT
              const sale = Number(p?.salePrice);
              const hasSale = !Number.isNaN(sale) && sale > 0;

              const hasDiscount =
                !!p?.hasDiscount || Number(p?.discountPercent || 0) > 0;
              const discountPercent = Math.max(
                0,
                Math.min(95, Number(p?.discountPercent || 0))
              );

              const basePrice = getBasePriceValue(p);
              const finalPrice = getFinalPriceValue(p);

              // show strike only when final differs from base (and base > 0)
              const showStrike = !hasSale && basePrice > 0 && finalPrice !== basePrice;

              return (
                <div className="ps-prod" key={p.id}>
                  <div className="ps-prod-img">
                    {img ? <img src={img} alt={title} /> : <div className="ps-ph">📦</div>}
                  </div>

                  <div className="ps-prod-body">
                    <div className="ps-prod-title">{title}</div>

                    <div className="ps-prod-sub">
                      {hasSale ? (
                        <>
                          {basePrice > 0 ? (
                            <span className="ps-strike">{moneyGHS(basePrice)}</span>
                          ) : null}{" "}
                          <span className="ps-badge">SALE</span> <b>{moneyGHS(finalPrice)}</b>
                        </>
                      ) : hasDiscount && discountPercent > 0 ? (
                        <>
                          {showStrike ? (
                            <>
                              <span className="ps-strike">{moneyGHS(basePrice)}</span>{" "}
                              <span className="ps-badge">-{discountPercent}%</span>{" "}
                            </>
                          ) : (
                            <span className="ps-badge">-{discountPercent}%</span>
                          )}
                          <b>{moneyGHS(finalPrice)}</b>
                        </>
                      ) : (
                        <b>{moneyGHS(finalPrice)}</b>
                      )}
                      {" • "}Stock: {Number(p.stock || 0)}
                      {safe(p?.categoryName) ? ` • ${safe(p.categoryName)}` : ""}
                      {safe(p?.productType) ? ` • ${safe(p.productType)}` : ""}
                    </div>

                    <div className="ps-actions">
                      <button className="ps-chip" onClick={() => toggleActive(p)}>
                        {active ? "Active" : "Hidden"}
                      </button>

                      <button className="ps-chip" onClick={() => goSellerView(p)}>
                        View
                      </button>

                      <button className="ps-chip" onClick={() => goSellerEdit(p)}>
                        Edit
                      </button>

                      <button className="ps-chip ps-danger" onClick={() => openConfirm(p)}>
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* ✅ QR MODAL (in-app) */}
      {qrOpen ? (
        <div className="ps-modal-overlay" onClick={closeQr}>
          <div className="ps-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ps-modal-title">Your Store QR Code</div>

            <div className="ps-qr-box">
              <div className="ps-qr-card">
                <QRCode value={publicStoreUrl} size={180} />
              </div>

              <input className="ps-input" readOnly value={publicStoreUrl} />
              <div className="ps-note">
                Scan to open your store link. (On localhost, it works only on the same
                device/network.)
              </div>
            </div>

            <div className="ps-modal-actions">
              <button className="ps-soft" onClick={copyPublicLink}>
                Copy Link
              </button>
              <button
                className="ps-soft"
                onClick={() => window.open(publicStoreUrl, "_blank")}
              >
                Open
              </button>
              <button className="ps-primary" onClick={closeQr}>
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* ✅ IN-APP CONFIRM MODAL (no browser popup) */}
      {confirmOpen ? (
        <div className="ps-modal-overlay" onClick={closeConfirm}>
          <div className="ps-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ps-modal-title">Delete product?</div>
            <div className="ps-modal-text">
              Are you sure you want to delete{" "}
              <b>{safe(confirmProduct?.title) || "this product"}</b>?
              <div className="ps-modal-sub">This cannot be undone.</div>
            </div>

            <div className="ps-modal-actions">
              <button className="ps-soft" onClick={closeConfirm} disabled={deleting}>
                Cancel
              </button>
              <button className="ps-primary" onClick={confirmDelete} disabled={deleting}>
                {deleting ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const css = `
.ps-page{min-height:100vh;background:#f3f6ff;padding:16px;font-family:Inter,system-ui,Segoe UI,Arial;color:#0f172a;}
.ps-wrap{max-width:980px;margin:0 auto;display:grid;gap:12px;}
.ps-top{display:flex;align-items:center;justify-content:space-between;gap:10px;}
.ps-top-actions{display:flex;align-items:center;gap:10px;}
.ps-back{border:1px solid #e7ecf6;background:#fff;border-radius:12px;padding:10px 12px;font-weight:900;cursor:pointer;}
.ps-title{font-weight:1100;font-size:20px;}
.ps-primary{border:0;background:#2f61c5;color:#fff;border-radius:12px;padding:10px 12px;font-weight:1000;cursor:pointer;box-shadow:0 10px 22px rgba(47,97,197,0.18);}
.ps-admin{border:1px solid #e7ecf6;background:#fff;border-radius:12px;padding:10px 12px;font-weight:1000;cursor:pointer;}
.ps-soft{border:1px solid #e7ecf6;background:#f7fbff;border-radius:12px;padding:8px 12px;font-weight:900;cursor:pointer;}
.ps-msg{border:1px solid #e7ecf6;background:#fff;border-radius:14px;padding:12px;font-weight:900;}
.ps-msg.is-err{border-color:rgba(255,70,120,0.35);background:rgba(255,70,120,0.06);color:#b91c1c;}
.ps-card{background:#fff;border:1px solid #e7ecf6;border-radius:18px;padding:12px;box-shadow:0 10px 26px rgba(15,23,42,0.06);display:grid;gap:10px;}
.ps-row{display:flex;align-items:center;justify-content:space-between;gap:10px;}
.ps-subtitle{font-weight:1100;}
.ps-link-actions{display:flex;gap:10px;align-items:center;}
.ps-input{width:100%;border:1px solid #cbd5e1;border-radius:12px;padding:10px;font-weight:900;outline:none;background:#fff;}
.ps-note{color:#64748b;font-weight:900;font-size:12px;}
.ps-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px;}
.ps-prod{background:#fff;border:1px solid #e7ecf6;border-radius:18px;overflow:hidden;box-shadow:0 10px 26px rgba(15,23,42,0.06);display:grid;grid-template-columns:110px 1fr;}
.ps-prod-img{background:#eef2ff;display:grid;place-items:center;}
.ps-prod-img img{width:100%;height:100%;object-fit:cover;}
.ps-ph{font-size:28px;}
.ps-prod-body{padding:12px;display:grid;gap:8px;}
.ps-prod-title{font-weight:1100;}
.ps-prod-sub{color:#64748b;font-weight:900;font-size:12px;line-height:1.35;}
.ps-actions{display:flex;gap:10px;flex-wrap:wrap;margin-top:6px;}
.ps-chip{border:1px solid #e7ecf6;background:#f7fbff;border-radius:999px;padding:8px 10px;font-weight:1000;cursor:pointer;}
.ps-danger{background:#fff1f2;border-color:#fecdd3;color:#be123c;}
.ps-strike{text-decoration:line-through;opacity:.7;margin-right:6px;}
.ps-badge{display:inline-block;border:1px solid #fde68a;background:#fffbeb;color:#92400e;border-radius:999px;padding:2px 8px;font-weight:1100;font-size:12px;margin-right:6px;}

/* ✅ modal */
.ps-modal-overlay{position:fixed;inset:0;background:rgba(15,23,42,.55);display:grid;place-items:center;padding:16px;z-index:9999;}
.ps-modal{width:min(420px,100%);background:#fff;border:1px solid #e7ecf6;border-radius:18px;box-shadow:0 20px 60px rgba(0,0,0,.22);padding:14px;display:grid;gap:10px;}
.ps-modal-title{font-weight:1100;font-size:16px;}
.ps-modal-text{color:#0f172a;font-weight:900;line-height:1.4;}
.ps-modal-sub{margin-top:6px;color:#64748b;font-weight:900;font-size:12px;}
.ps-modal-actions{display:flex;gap:10px;justify-content:flex-end;margin-top:6px;}
.ps-primary:disabled,.ps-soft:disabled{opacity:.6;cursor:not-allowed;}

/* ✅ QR box */
.ps-qr-box{display:grid;gap:10px;}
.ps-qr-card{width:max-content;background:#fff;border:1px solid #e7ecf6;border-radius:14px;padding:12px;}

@media (max-width: 820px){
  .ps-grid{grid-template-columns:1fr;}
  .ps-prod{grid-template-columns:95px 1fr;}
}
`;
